import {
  AccessConfigResponse,
  BusinessCompanyDetails,
  Company,
  BooklaAdminCompanySettings,
  CompanyContact,
  CompanyContent,
  CompanyDetails,
  CompanyInvoice,
  CompanyLegalInfo,
  CompanySetupParams,
  CompanySubscriptionPriceType,
  CompanySubscriptionType,
  CompanyTranslation,
  SubscriptionCheckoutURL,
} from '../types/company';
import { handleResponse } from './handleResponse';
import apiAxios from './apiAxios';
import { Image } from '../types/common';
import { UTMParams } from '../types/UTMParams';
import {
  CustomTerm,
  CustomTermDetails,
  CustomTermTranslation,
} from '../types/terms';

const companyApi = {
  async listMyCompanies(): Promise<Company[]> {
    return apiAxios.get('/api/v2/user/me/companies').then(handleResponse);
  },
  async getCompanyDetails(companyId: string): Promise<CompanyDetails> {
    return apiAxios
      .get(`/api/portal/company/${companyId}`)
      .then(handleResponse);
  },
  async updateCompanyContent(companyId: string, content: CompanyContent) {
    return apiAxios.put(`/api/portal/company/${companyId}/edit/content`, {
      categoryId: content.category.id,
      country: content.country,
      genderPreference: content.genderPreference,
      paymentOption: content.paymentOption,
      workHours: content.workHours,
      address: content.address,
      latitude: content.latitude,
      longitude: content.longitude,
    });
  },
  async updateCompanyContacts(companyId: string, contacts: CompanyContact[]) {
    return apiAxios.put(`/api/portal/company/${companyId}/edit/contacts`, {
      contacts,
    });
  },
  async updateCompanyTranslations(
    companyId: string,
    translations: Map<string, CompanyTranslation>
  ) {
    return apiAxios.put(`/api/portal/company/${companyId}/edit/translations`, {
      translations: Object.fromEntries(translations),
    });
  },
  async updateCompanyImage(companyId: string, imageID: string): Promise<Image> {
    return apiAxios
      .put(`/api/portal/company/${companyId}/edit/image`, { imageID })
      .then(handleResponse);
  },
  async updateBooklaAdminCompanySettings(
    companyId: string,
    settings: BooklaAdminCompanySettings
  ) {
    return apiAxios.put(`/api/portal/company/${companyId}/edit/adminSettings`, {
      montonioPaymentsEnabled: settings.montonioPaymentsEnabled,
      smsCampaignsEnabled: settings.smsCampaignsEnabled,
      applyFirstPurchaseFee: settings.applyFirstPurchaseFee,
    });
  },
  async createCompany(data: {
    name: string;
    email: string;
    categoryId?: string;
    cityGeoId: string;
    utmParams?: UTMParams | null;
  }): Promise<CompanyDetails> {
    return apiAxios.post(`/api/portal/company`, data).then(handleResponse);
  },
  async listCompanyInvoices(companyId: string): Promise<CompanyInvoice[]> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/invoices`)
      .then(handleResponse);
  },
  async downloadInvoice(
    companyId: string,
    invoiceNumber: string
  ): Promise<Blob> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/invoices/${invoiceNumber}`, {
        responseType: 'blob',
      })
      .then(handleResponse);
  },
  async listCompanyTerms(companyId: string): Promise<CustomTerm[]> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/terms`)
      .then(handleResponse);
  },
  async getCompanyTerm(
    companyId: string,
    termId: string
  ): Promise<CustomTermDetails> {
    return apiAxios
      .get(`/api/portal/company/${companyId}/terms/${termId}`)
      .then(handleResponse);
  },
  async addNewCustomTerm(
    companyId: string,
    title: string,
    translations: Map<string, CustomTermTranslation>
  ): Promise<CustomTermDetails> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/terms`, {
        title,
        translations: Object.fromEntries(translations),
      })
      .then(handleResponse);
  },
  async editCustomTerm(
    companyId: string,
    termId: string,
    title: string,
    translations: Map<string, CustomTermTranslation>
  ): Promise<CustomTermDetails> {
    return apiAxios
      .put(`/api/portal/company/${companyId}/terms/${termId}`, {
        title,
        translations: Object.fromEntries(translations),
      })
      .then(handleResponse);
  },
  async deleteCustomTerm(companyId: string, termId: string) {
    return apiAxios.delete(`/api/portal/company/${companyId}/terms/${termId}`);
  },
  async getStripeLink(
    companyId: string,
    redirectUrl: string
  ): Promise<{ link: string }> {
    return apiAxios
      .get(`/api/companies/${companyId}/stripe`, {
        params: {
          redirectUrl,
        },
      })
      .then(handleResponse);
  },
  async confirmStripe(
    companyId: string,
    state: string,
    code: string
  ): Promise<void> {
    return apiAxios
      .put(`/api/companies/${companyId}/stripe`, undefined, {
        params: {
          state,
          code,
        },
      })
      .then(handleResponse);
  },
  async getSubscriptionLink(
    companyId: string,
    data: {
      priceId: CompanySubscriptionPriceType;
      cancelUrl: string;
      successUrl: string;
      type: CompanySubscriptionType;
    }
  ): Promise<SubscriptionCheckoutURL> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/subscriptions`, data)
      .then(handleResponse);
  },
  async getStripeBillingURL(
    companyId: string,
    returnUrl: string
  ): Promise<{ url: string; stripeId: string }> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/billingPortal`, { returnUrl })
      .then(handleResponse);
  },
  async saveLegalInfo(
    companyId: string,
    data: CompanyLegalInfo
  ): Promise<CompanyLegalInfo> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/agreements`, data)
      .then(handleResponse);
  },
  async updateLegalInfo(
    companyId: string,
    agreementId: string,
    data: CompanyLegalInfo
  ): Promise<CompanyLegalInfo> {
    return apiAxios
      .put(`/api/portal/company/${companyId}/agreements/${agreementId}`, data)
      .then(handleResponse);
  },
  async getLegalInfo(companyId: string): Promise<CompanyLegalInfo | undefined> {
    return apiAxios
      .get<CompanyLegalInfo | undefined>(
        `/api/portal/company/${companyId}/agreements`
      )
      .then((response) => {
        if (!response.data?.companyName) {
          return undefined;
        } else {
          return response.data;
        }
      });
  },
  async updateCompanyEnabled(
    companyId: string,
    enabled: boolean
  ): Promise<void> {
    return apiAxios
      .put(`/api/portal/company/${companyId}/edit/enable`, {
        enabled,
      })
      .then(handleResponse);
  },
  async getBusinessCompanyDetails(
    companyId: string
  ): Promise<BusinessCompanyDetails> {
    return apiAxios
      .get(`/api/companies/${companyId}/schedule/filters`)
      .then(handleResponse);
  },
  async setupCompany(
    companyId: string,
    data: CompanySetupParams
  ): Promise<void> {
    return apiAxios
      .post(`/api/portal/company/${companyId}/setup`, data)
      .then(handleResponse);
  },
  getAccessConfig: async (companyId: string): Promise<AccessConfigResponse> =>
    apiAxios
      .get(`/api/portal/company/${companyId}/accessConfig`)
      .then(handleResponse),
  async getShortUrl(url: string): Promise<{
    longURL: string;
    shortURL: string;
  }> {
    return apiAxios
      .get('/api/portal/shorten', { params: { url } })
      .then(handleResponse);
  },
};

export default companyApi;
